import { useMemo } from 'react'

import { useSession } from '@/entities/session'

import { useGetProductReviewsQuery, useGetProductReviewSummaryQuery } from '../api/product-reviews-api'

import type { ProductReviewsResponse, ProductReviewsSummary } from './types'
import { useProductReviewsData } from './use-product-reviews-data'

type ProductPageReviewsProduct = {
	KOD?: string | number
	REVIEWS_SUMMARY?: {
		RATING: number
		COUNT: number
	}
	IMAGES_RESIZE?: string[]
	IMAGES?: string[]
	IMAGE?: string
}

type ProductPageReviewsCrossLinkGroup = {
	type: string
	values: Array<{ kod: string | number }>
}

export type ProductCurrentReviewSummary = {
	RATING: number
	COUNT: number
	rawSummary: ProductReviewsSummary
	hasOwnCurrentVariantReview: boolean
}

type UseProductPageReviewsParams = {
	product?: ProductPageReviewsProduct | null
	crossLinks?: ProductPageReviewsCrossLinkGroup[]
	isCrossLinksLoading?: boolean
	isTabActive?: boolean
}

const COLOR_CROSS_LINK_TYPE = 'color'

const getProductReviewCodes = (
	product?: ProductPageReviewsProduct | null,
	crossLinks: ProductPageReviewsCrossLinkGroup[] = []
) =>
	Array.from(
		new Set([
			Number(product?.KOD),
			...crossLinks
				.filter(group => group.type === COLOR_CROSS_LINK_TYPE)
				.flatMap(group => group.values.map(variant => Number(variant.kod)))
		])
	).filter(Number.isFinite)

const getProductImages = (product?: ProductPageReviewsProduct | null) =>
	product?.IMAGES_RESIZE ?? product?.IMAGES ?? (product?.IMAGE ? [product.IMAGE] : [])

const getFallbackReviewsData = (
	product: ProductPageReviewsProduct,
	reviewsSummary?: ProductCurrentReviewSummary
): ProductReviewsResponse => {
	const summary = reviewsSummary ?? product.REVIEWS_SUMMARY
	const total = summary?.COUNT ?? 0
	const average = summary?.RATING ?? 0
	const rawSummary = reviewsSummary?.rawSummary
	const productImages = getProductImages(product)
	const image = productImages[0]

	return {
		summary: rawSummary ?? {
			average,
			total,
			distribution: [5, 4, 3, 2, 1].map(rating => ({ rating, count: 0 })),
			criteria: []
		},
		variants: [
			{ id: 'all', name: 'Все модели', image, images: productImages.slice(0, 2) },
			{ id: 'current', name: 'Этот вариант', image, images: productImages.slice(0, 1) }
		],
		gallery: [],
		reviews: [],
		hasMore: total > 0
	}
}

const useProductPageReviews = ({
	product,
	crossLinks = [],
	isCrossLinksLoading = false,
	isTabActive = false
}: UseProductPageReviewsParams) => {
	const productCode = Number(product?.KOD)
	const productCodes = useMemo(() => getProductReviewCodes(product, crossLinks), [crossLinks, product])
	const productImages = useMemo(() => getProductImages(product), [product])
	const { currentSession } = useSession()
	const token = currentSession?.token || undefined
	const hasProductCode = Number.isFinite(productCode) && productCodes.length > 0
	const canLoadSummary = hasProductCode && !isCrossLinksLoading
	const previewArgs = useMemo(
		() => ({
			productCode,
			productCodes: [productCode],
			sort: 'newest' as const,
			variant: 'all' as const,
			limit: 1,
			currentPage: 1,
			productImages: [],
			includeSummary: false,
			includeMedia: false
		}),
		[productCode]
	)
	const summaryQuery = useGetProductReviewSummaryQuery(productCodes, {
		skip: !canLoadSummary,
		refetchOnFocus: false,
		refetchOnReconnect: false,
		refetchOnMountOrArgChange: false
	})
	const previewQuery = useGetProductReviewsQuery(previewArgs, {
		skip: !canLoadSummary,
		refetchOnFocus: false,
		refetchOnReconnect: false,
		refetchOnMountOrArgChange: false
	})
	const summary = useMemo<ProductCurrentReviewSummary | undefined>(() => {
		if (!summaryQuery.data || !previewQuery.data) return undefined

		const reviewsCount = previewQuery.data.totalCount ?? 0

		return {
			RATING: Math.round(summaryQuery.data.average * 10) / 10,
			COUNT: reviewsCount,
			rawSummary: { ...summaryQuery.data, total: reviewsCount },
			hasOwnCurrentVariantReview: previewQuery.data.reviews.some(review => review.isUserReview)
		}
	}, [previewQuery.data, summaryQuery.data])
	const isSummaryLoading =
		hasProductCode &&
		(isCrossLinksLoading ||
			(canLoadSummary &&
				(summaryQuery.isLoading || summaryQuery.isFetching || previewQuery.isLoading || previewQuery.isFetching)))
	const shouldLoadReviewsData = isTabActive && !!summary
	const reviewsData = useProductReviewsData({
		productCode,
		productCodes,
		productImages,
		token,
		cachedSummary: summary?.rawSummary,
		enabled: shouldLoadReviewsData
	})
	const initialData = useMemo(() => {
		if (!product) return undefined

		const data = reviewsData.data ?? getFallbackReviewsData(product, summary)
		if (!summary) return data

		return {
			...data,
			summary: {
				...data.summary,
				total: summary.COUNT
			}
		}
	}, [product, reviewsData.data, summary])

	return useMemo(
		() => ({
			summary,
			isSummaryLoading,
			tabData: {
				initialData,
				isLoading: isTabActive && (!summary || reviewsData.isLoading),
				productCodes,
				productImages,
				token
			}
		}),
		[initialData, isTabActive, isSummaryLoading, productCodes, productImages, reviewsData.isLoading, summary, token]
	)
}

export const useProductCurrentReviewSummary = (
	product?: ProductPageReviewsProduct | null,
	crossLinks: ProductPageReviewsCrossLinkGroup[] = [],
	isCrossLinksLoading = false
) => {
	const { summary, isSummaryLoading } = useProductPageReviews({ product, crossLinks, isCrossLinksLoading })

	return {
		summary,
		isLoading: isSummaryLoading
	}
}

export const useProductReviewsTabData = (
	product: ProductPageReviewsProduct,
	crossLinks: ProductPageReviewsCrossLinkGroup[] = [],
	enabled = true,
	reviewsSummary?: ProductCurrentReviewSummary
) => {
	const { currentSession } = useSession()
	const token = currentSession?.token || undefined
	const productCodes = useMemo(() => getProductReviewCodes(product, crossLinks), [crossLinks, product])
	const productImages = useMemo(() => getProductImages(product), [product])
	const shouldLoadReviewsData = enabled && !!reviewsSummary
	const reviewsData = useProductReviewsData({
		productCode: product.KOD,
		productCodes,
		productImages,
		token,
		cachedSummary: reviewsSummary?.rawSummary,
		enabled: shouldLoadReviewsData
	})
	const initialData = useMemo(() => {
		const data = reviewsData.data ?? getFallbackReviewsData(product, reviewsSummary)
		if (!reviewsSummary) return data

		return {
			...data,
			summary: {
				...data.summary,
				total: reviewsSummary.COUNT
			}
		}
	}, [product, reviewsData.data, reviewsSummary])

	return {
		initialData,
		isLoading: enabled && (!reviewsSummary || reviewsData.isLoading),
		productCodes,
		productImages,
		token,
		hasOwnCurrentVariantReview: reviewsSummary?.hasOwnCurrentVariantReview ?? false
	}
}
